import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useState, type FC } from "react";
import { Input } from "../ui/input";
import useDebounce from "@/hooks/useDebounce";
import { AutocompleteResultType } from "@/API";
import Text from "../Text";
import {
  autoCompleteCategoriesState,
  searchForAutoCompleteCategories,
} from "@/store/autoCompleteCategoriesState";
import { useTranslation } from "react-i18next";

type Props = {
  onChange?: (value: string[]) => void;
  defaultValue?: string[];
};

const SearchCategories: FC<Props> = ({ onChange, defaultValue }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [value, setValue] = useState<string[]>(defaultValue ?? []);

  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = autoCompleteCategoriesState();

  useEffect(() => {
    if (debouncedSearch.length > 2) {
      searchForAutoCompleteCategories({ search: debouncedSearch });
    }
  }, [debouncedSearch]);

  let categoriesAutoComplet: string[] = [];

  if (data && data.autocompleteCategory) {
    categoriesAutoComplet = data.autocompleteCategory
      .filter(
        (value) => value && value.type === AutocompleteResultType.CATEGORY,
      )
      .map((value) => value?.value) as string[];
  }

  categoriesAutoComplet = categoriesAutoComplet.filter(
    (category) => !value.find((v) => v === category),
  );

  useEffect(() => {
    onChange?.(value);
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryFilled"
          role="combobox"
          aria-expanded={open}
          className="w-[100%] justify-between bg-[white]"
          style={{
            border: "1px solid #B3B9C4",
          }}
        >
          <Text className="truncate">
            {value.length > 0 ? value.join(", ") : t("select_categories")}
          </Text>

          <ChevronDownIcon
            width={24}
            height={24}
            color="#758195"
            className="ml-2 h-4 w-4 shrink-0"
            style={{
              transition: "transform 0.3s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[355px] p-[12px]">
        <Input
          placeholder={t("research_category")}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <div className="flex flex-col gap-[8px] my-[8px]">
          {value.map((countrie, index) => (
            <button
              key={`value-${countrie}-${index}`}
              onClick={() =>
                setValue((prevState) => {
                  const copyPrevState = [...prevState];
                  const countrieIndexToRemove = copyPrevState.indexOf(countrie);

                  if (countrieIndexToRemove !== -1)
                    copyPrevState.splice(countrieIndexToRemove, 1);

                  return copyPrevState;
                })
              }
              className="flex items-center gap-[8px] px-[12px] py-[8px] hover:bg-[#F1F2F4]"
            >
              <CheckIcon height={16} width={16} />
              <Text fontSize={16} fontWeight={400}>
                {countrie}
              </Text>
            </button>
          ))}
        </div>
        {value.length > 0 && categoriesAutoComplet.length > 0 && <hr />}
        {isLoading && (
          <div className="mt-[12px]">
            <Text>{t("searching_process")}</Text>
          </div>
        )}
        <div className="flex flex-col gap-[8px] mt-[8px]">
          {categoriesAutoComplet.map((category, index) => (
            <button
              key={`auto-complete-${category}-${index}`}
              onClick={() => {
                setValue((prevState) => [...prevState, category]);
                setSearch("");
              }}
              className="flex px-[12px] py-[8px] hover:bg-[#F1F2F4]"
            >
              <Text fontSize={16} fontWeight={400}>
                {category}
              </Text>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchCategories;
